import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';

export type ResetScope = 'financial' | 'complete';

export interface ClientDataCounts {
  transactions: number;
  documents: number;
  accounts: number;
  categories: number;
  cost_centers: number;
  counterparties: number;
  agreements: number;
  agreement_payments: number;
  treatment_packages: number;
  patients: number;
  card_splits: number;
  card_machines: number;
  billing_invoices: number;
  client_events: number;
}

interface RpcResponse {
  success: boolean;
  counts?: ClientDataCounts;
  message?: string;
  sqlstate?: string;
}

/**
 * Contagens vivas de cada tabela operacional do cliente. Usado para
 * mostrar preview antes de confirmar reset.
 */
export function useClientDataCounts(clientId: string | null) {
  return useQuery({
    queryKey: ['client-data-counts', clientId],
    enabled: !!clientId,
    staleTime: 10_000,
    queryFn: async (): Promise<ClientDataCounts | null> => {
      if (!clientId) return null;
      const { data, error } = await supabase.rpc('count_client_data', { p_client_id: clientId });
      if (error) throw error;
      const res = data as unknown as RpcResponse;
      if (!res.success) throw new Error(res.message ?? 'Falha ao buscar contagens');
      return res.counts ?? null;
    },
  });
}

/**
 * Executa o reset no escopo escolhido. Após sucesso invalida todas as
 * queries do app (a tela atual pode estar mostrando dados que acabaram
 * de ser apagados — o refetch atualiza tudo).
 */
export function useResetClientData() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      clientId,
      scope,
    }: {
      clientId: string;
      scope: ResetScope;
    }): Promise<ClientDataCounts> => {
      const { data, error } = await supabase.rpc('reset_client_data', {
        p_client_id: clientId,
        p_scope: scope,
      });
      if (error) throw error;
      const res = data as unknown as RpcResponse;
      if (!res.success) {
        throw new Error(res.message ?? 'Falha ao executar reset');
      }
      return res.counts ?? ({} as ClientDataCounts);
    },
    onSuccess: () => {
      // Reset é raro e mexe em muitas tabelas — invalida tudo.
      qc.invalidateQueries();
      toast.success('Reset concluído. Os dados foram apagados.');
    },
    onError: (e: Error) => toast.error('Erro no reset: ' + e.message),
  });
}
